/**
 * Wrap a Firebase callable so browser failures include a user-actionable reason.
 *
 * The Firebase SDK remains responsible for enforcing the configured request
 * deadline. This wrapper preserves the callable response shape and error code
 * while replacing opaque transient messages with retry guidance.
 *
 * @param {(payload: unknown) => Promise<unknown>} callable - Firebase callable.
 * @param {{ action: string, timeoutMs: number }} options - Error context.
 * @returns {(payload: unknown) => Promise<unknown>} Wrapped callable.
 */
export function withActionableCallableErrors(callable, { action, timeoutMs }) {
  return async (payload) => {
    try {
      return await callable(payload);
    } catch (cause) {
      const rawCode = String(cause?.code || '').trim();
      const code = rawCode.replace(/^functions\//, '');
      const timeoutSeconds = Math.ceil(Number(timeoutMs) / 1000);
      const transientCodes = new Set([
        'deadline-exceeded',
        'internal',
        'network-request-failed',
        'resource-exhausted',
        'unavailable',
      ]);
      let message = cause?.message || `${action} failed.`;

      if (code === 'deadline-exceeded') {
        message = `${action} timed out after ${timeoutSeconds} seconds. Please retry.`;
      } else if (transientCodes.has(code)) {
        message = `${action} could not reach the service (${code}). Please retry.`;
      }

      const error = new Error(message, { cause });
      error.name = 'CallableActionError';
      error.code = rawCode || null;
      throw error;
    }
  };
}
